import { Mob } from './Mob';

export class RangedMob extends Mob {
    constructor(scene, x, y, config = {}) {
        // Set default ranged-specific configurations
        const rangedConfig = {
            enemyType: 'ranged',
            health: config.health || 60,
            speed: config.speed || 40,
            damage: config.damage || 12,
            attackRange: config.attackRange || 120, // Longer range for ranged attacks
            detectionRange: config.detectionRange || 180,
            attackCooldown: config.attackCooldown || 2500,
            shouldPatrol: config.shouldPatrol !== false,
            ...config
        };

        super(scene, x, y, rangedConfig);
        
        // Ranged-specific properties
        this.projectileSpeed = config.projectileSpeed || 200;
        this.projectileSprite = config.projectileSprite || 'arrow';
        this.keepDistance = config.keepDistance || 80; // Distance to maintain from player
        this.isBackingAway = false;
        this.projectiles = []; // Track active projectiles
        
        // Debug properties
        this.showDebugLine = config.showDebugLine || false;
        this.debugLine = null;
    }

    moveTowardsPlayer() {
        const player = this.getPlayer();
        if (!player) return;

        const distance = this.getDistanceToPlayer();
        const direction = this.getDirectionToPlayer();
        this.facingDirection = direction.x > 0 ? 1 : -1;

        // Ranged mobs try to maintain optimal distance
        if (distance < this.keepDistance) {
            // Too close - back away
            this.isBackingAway = true;
            this.setVelocity(-direction.x * this.speed, -direction.y * this.speed);
        } else if (distance > this.attackRange) {
            // Too far - move closer but stop at keepDistance
            this.isBackingAway = false;
            this.setVelocity(direction.x * this.speed * 0.7, direction.y * this.speed * 0.7);
        } else {
            // In optimal range - stop moving
            this.isBackingAway = false;
            this.setVelocity(0, 0);
        }
    }

    isPlayerInAttackRange() {
        const distance = this.getDistanceToPlayer();
        return distance <= this.attackRange && distance >= this.keepDistance * 0.5 && !this.isDead;
    }

    dealDamageToPlayer() {
        // Ranged mobs shoot projectiles instead of direct damage
        this.fireProjectile();
    }

    fireProjectile() {
        const player = this.getPlayer();
        if (!player) return;

        // Play attack sound immediately when firing
        this.playAttackSound();

        // Calculate trajectory to player
        const direction = this.getDirectionToPlayer();
        
        // Update facing direction based on the direction we're firing
        this.facingDirection = direction.x > 0 ? 1 : -1;
        
        // Update debug line if enabled
        this.updateDebugLine(direction);

        // Create projectile
        const projectile = this.scene.add.sprite(this.x, this.y - (this.sprite.height * 0.3), this.projectileSprite);
        this.scene.physics.world.enable(projectile);
        projectile.body.setSize(8, 8);
        
        projectile.body.setVelocity(
            direction.x * this.projectileSpeed,
            direction.y * this.projectileSpeed
        );

        // Rotate projectile based on direction (for skeleton archer arrows)
        if (this.projectileSprite === 'skeleton_archer_projectile') {
            if (this.facingDirection > 0) {
                // Facing right: rotate 90 degrees clockwise
                projectile.setRotation(Math.PI / 2);
            } else {
                // Facing left: rotate 90 degrees counter-clockwise
                projectile.setRotation(-Math.PI / 2);
            }
        }

        // Set projectile properties
        projectile.damage = this.damage;
        projectile.owner = this;
        projectile.setDepth(12);

        // Add to projectiles array
        this.projectiles.push(projectile);

        // Set up projectile collision and cleanup
        this.setupProjectile(projectile);

        // Play projectile animation if available
        if (this.scene.anims.exists(`${this.projectileSprite}_move`)) {
            projectile.play(`${this.projectileSprite}_move`);
        }
    }

    updateDebugLine(direction) {
        if (!this.showDebugLine) return;
        
        // Remove existing debug line
        if (this.debugLine) {
            this.debugLine.destroy();
        }
        
        // Create new debug line showing aim direction
        // Use center-based positioning to match the direction calculation
        const lineLength = 100; // Length of the debug line
        const startX = this.x;
        const startY = this.y - (this.sprite.height * 0.5); // Use center position like direction calculation
        const endX = startX + (direction.x * lineLength);
        const endY = startY + (direction.y * lineLength);
        
        // Create a line using Phaser graphics
        this.debugLine = this.scene.add.graphics();
        this.debugLine.lineStyle(2, 0xff0000, 0.8); // Red line with some transparency
        this.debugLine.lineBetween(startX, startY, endX, endY);
        this.debugLine.setDepth(15); // Above most other objects
        
        // Add arrowhead to show direction
        const arrowSize = 6;
        const angle = Math.atan2(direction.y, direction.x);
        
        // Calculate arrowhead points
        const arrowX1 = endX - arrowSize * Math.cos(angle - Math.PI / 6);
        const arrowY1 = endY - arrowSize * Math.sin(angle - Math.PI / 6);
        const arrowX2 = endX - arrowSize * Math.cos(angle + Math.PI / 6);
        const arrowY2 = endY - arrowSize * Math.sin(angle + Math.PI / 6);
        
        // Draw arrowhead
        this.debugLine.lineBetween(endX, endY, arrowX1, arrowY1);
        this.debugLine.lineBetween(endX, endY, arrowX2, arrowY2);
        
        // Auto-remove debug line after a short time
        this.scene.time.delayedCall(1000, () => {
            if (this.debugLine) {
                this.debugLine.destroy();
                this.debugLine = null;
            }
        });
    }

    setupProjectile(projectile) {
        // Collision with player
        this.scene.physics.add.overlap(projectile, this.getPlayer()?.sprite || this.getPlayer(), 
            (proj, playerSprite) => {
                this.onProjectileHitPlayer(proj);
            });

        // Collision with scene collision bodies (bricks, walls, etc.)
        if (this.scene.collisionBodies) {
            this.scene.physics.add.collider(projectile, this.scene.collisionBodies, 
                (proj, collisionBody) => {
                    this.onProjectileHitWall(proj, collisionBody);
                });
        }

        // Collision with world bounds - disable sliding by destroying immediately
        projectile.body.setCollideWorldBounds(true);
        projectile.body.onWorldBounds = true;
        projectile.body.world.on('worldbounds', (event, body) => {
            if (body.gameObject === projectile) {
                this.onProjectileHitWall(projectile, null);
            }
        });

        // Auto-destroy after time
        this.scene.time.delayedCall(3000, () => {
            this.destroyProjectile(projectile);
        });
    }

    onProjectileHitPlayer(projectile) {
        const player = this.getPlayer();
        if (player && player.modifyHealth) {
            player.modifyHealth(-projectile.damage); // Use negative value to reduce health
        }
        
        this.destroyProjectile(projectile);
    }

    onProjectileHitWall(projectile, collisionBody) {
        // Play ranged hit sound (arrow stuck, etc.)
        this.playRangedHitSound();

        // Stop the projectile movement completely
        projectile.body.setVelocity(0, 0);
        projectile.body.setImmovable(true);
        
        // CRITICAL: Disable world bounds collision to prevent sliding
        projectile.body.setCollideWorldBounds(false);
        
        // Remove the world bounds event listener to prevent further events
        if (projectile.body.world && projectile.body.onWorldBounds) {
            projectile.body.onWorldBounds = false;
        }

        // Remove from tracking array
        const index = this.projectiles.indexOf(projectile);
        if (index > -1) {
            this.projectiles.splice(index, 1);
        }

        // Fade away after 5 seconds
        this.scene.time.delayedCall(5000, () => {
            if (projectile && projectile.destroy) {
                // Fade out effect
                this.scene.tweens.add({
                    targets: projectile,
                    alpha: 0,
                    duration: 500,
                    onComplete: () => {
                        if (projectile && projectile.destroy) {
                            projectile.destroy();
                        }
                    }
                });
            }
        });
    }

    destroyProjectile(projectile) {
        // Remove from tracking array
        const index = this.projectiles.indexOf(projectile);
        if (index > -1) {
            this.projectiles.splice(index, 1);
        }

        // Add hit effect
        if (projectile.x && projectile.y) {
            const hitEffect = this.scene.add.circle(projectile.x, projectile.y, 5, 0xffff00, 0.8);
            hitEffect.setDepth(15);
            this.scene.tweens.add({
                targets: hitEffect,
                scaleX: 2,
                scaleY: 2,
                alpha: 0,
                duration: 200,
                onComplete: () => hitEffect.destroy()
            });
        }

        // Destroy projectile
        if (projectile && projectile.destroy) {
            projectile.destroy();
        }
    }

    destroy() {
        // Clean up debug line
        if (this.debugLine) {
            this.debugLine.destroy();
            this.debugLine = null;
        }
        
        // Clean up all projectiles
        this.projectiles.forEach(projectile => {
            if (projectile && projectile.destroy) {
                projectile.destroy();
            }
        });
        this.projectiles = [];

        super.destroy();
    }

    onDeath() {
        super.onDeath();
        
        // Ranged mobs might drop ranged-specific loot
        this.scene.events.emit('mobDropLoot', {
            mobType: this.mobType,
            enemyType: this.enemyType,
            position: { x: this.x, y: this.y },
            lootType: 'ranged'
        });
    }
}

// Specific ranged mob types
export class SkeletonArcherMob extends RangedMob {
    constructor(scene, x, y, config = {}) {
        super(scene, x, y, {
            mobType: 'skeleton_archer',
            projectileSprite: 'skeleton_archer_projectile',
            health: 50,
            speed: 35,
            damage: 15,
            attackRange: 140,
            keepDistance: 90,
            projectileSpeed: 250,
            attackCooldown: 2000,
            showDebugLine: false, // Enable debug line for skeleton arcahers
            // Audio configuration
            attackSounds: [ 'bow-fire-A1', 'bow-fire-A2'],
            damageSounds: ['zombie-hurt-1', 'zombie-hurt-2'],
            deathSounds: ['skeleton-knight-3'],
            rangedHitSounds: ['arrow-stuck'],
            ...config
        });
    }
}

export class PoisonZombieMob extends RangedMob {
    constructor(scene, x, y, config = {}) {
        super(scene, x, y, {
            mobType: 'poison_zombie',
            projectileSprite: 'poison_zombie_projectile',
            health: 65,
            speed: 30,
            damage: 10,
            attackRange: 100,
            keepDistance: 70,
            projectileSpeed: 180,
            attackCooldown: 3000,
            // Audio configuration
            attackSounds: ['poison-zombie-shoot-1', 'poison-zombie-shoot-2'],
            damageSounds: ['zombie-hurt-1', 'zombie-hurt-2'],
            deathSounds: ['zombie-death-1', 'zombie-death-2'],
            rangedHitSounds: ['poison-zombie-hit-1'],
            ...config
        });

        this.poisonDamage = 5;
        this.poisonDuration = 3000; // 3 seconds
    }

    onProjectileHitPlayer(projectile) {
        const player = this.getPlayer();
        if (player && player.modifyHealth) {
            const damageDirection = {
                x: projectile.body.velocity.x > 0 ? 1 : -1,
                y: projectile.body.velocity.y > 0 ? 0.3 : -0.3
            };
            
            // Initial damage
            player.modifyHealth(-projectile.damage); // Use negative value to reduce health
            
            // Apply poison effect
            this.applyPoisonEffect(player);
        }
        
        this.destroyProjectile(projectile);
    }

    applyPoisonEffect(player) {
        if (!player.poisonTimer) {
            player.poisonTimer = 0;
            player.poisonTotalDuration = this.poisonDuration;
            player.poisonTickDamage = this.poisonDamage;
            player.poisonTickInterval = 500; // Damage every 0.5 seconds
            player.poisonLastTick = 0;

            // Visual poison effect
            player.sprite?.setTint(0x44ff44); // Green tint for poison
        } else {
            // Refresh poison duration
            player.poisonTimer = 0;
        }
    }
}

// Stationary ranged mob (like a turret)
export class StationaryRangedMob extends RangedMob {
    constructor(scene, x, y, config = {}) {
        super(scene, x, y, {
            enemyType: 'stationary',
            speed: 0, // No movement
            shouldPatrol: false,
            health: 80,
            damage: 20,
            attackRange: 160,
            detectionRange: 200,
            attackCooldown: 1500,
            ...config
        });
    }

    moveTowardsPlayer() {
        // Stationary mobs don't move, just face the player
        const direction = this.getDirectionToPlayer();
        this.facingDirection = direction.x > 0 ? 1 : -1;
        this.setVelocity(0, 0);
    }

    isPlayerInAttackRange() {
        // Stationary mobs can attack from anywhere in their detection range
        return this.canSeePlayer();
    }
} 